// Thunks relacionados con suscripción de planes
import { createAsyncThunk } from "@reduxjs/toolkit";
import store from "@/app/store";
import api from "@/services/api";
import {
  setPagos,
  setPlanActual,
  setKlapOrderId,
  suscripcionSuccess,
  suscripcionFailure,
  setLoadingSuscripcion,
} from "./suscripcionSlice";

/**
 * 📝 SUSCRIPCIÓN - Obtener lista de pagos de planes por empresa
 * @param {number} idEmpresa - ID de la empresa
 * @returns {Promise<Array>} - Lista de pagos realizados
 */
export const getListPagosPlanPorEmpresaId = createAsyncThunk(
  "suscripcion/getListPagosPlanPorEmpresaId",
  async ({ idEmpresa }, { rejectWithValue }) => {
    try {
      store.dispatch(setLoadingSuscripcion(true));
      
      // GET /suscripciones/{idEmpresa}/pagos
      const response = await api.get(`/suscripciones/${idEmpresa}/pagos`);
      
      store.dispatch(setPagos(response.data));
      store.dispatch(setLoadingSuscripcion(false));
      
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al obtener pagos de planes";
      store.dispatch(suscripcionFailure(message));
      store.dispatch(setLoadingSuscripcion(false));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 SUSCRIPCIÓN - Iniciar proceso de pago de Plan con KLAP
 * Obtiene el klapOrderId para abrir el checkout de KLAP
 * 
 * @param {number} idEmpresa - ID de la empresa
 * @param {string} plan - Plan a pagar (1_mes, 3_meses, 12_meses)
 * @returns {Promise<{klapOrderId: string, uidOrdenPago: string}>} - Retorna el klapOrderId y uidOrdenPago
 */
export const payPlanWithKlap = createAsyncThunk(
  "suscripcion/payPlanWithKlap",
  async ({ idEmpresa, plan }, { rejectWithValue }) => {
    try {
      store.dispatch(setLoadingSuscripcion(true));
      
      // POST /suscripciones/pay-plan
      const response = await api.post(`/suscripciones/pay-plan`, {
        idEmpresa,
        plan,
      });
      
      // La respuesta debe contener { klapOrderId: "...", uidOrdenPago: "..." }
      if (!response.data?.klapOrderId) {
        throw new Error("No se recibió klapOrderId de la pasarela de pago");
      }
      
      store.dispatch(setKlapOrderId(response.data.klapOrderId));
      store.dispatch(setLoadingSuscripcion(false));
      
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || 
        error.response?.data?.error?.message || 
        "Error al iniciar el proceso de pago con KLAP";
      const errorCode = error.response?.data?.error?.code;
      
      store.dispatch(suscripcionFailure(message));
      store.dispatch(setLoadingSuscripcion(false));
      
      return rejectWithValue({ message, code: errorCode });
    }
  }
);

/**
 * 📝 SUSCRIPCIÓN - Actualizar información del plan actual de la empresa
 * Esta función se puede llamar después de obtener la información de la empresa
 * 
 * @param {object} planInfo - Información del plan { planActivo, planTipo, planVigencia }
 */
export const updatePlanActual = (planInfo) => (dispatch) => {
  dispatch(setPlanActual(planInfo));
};

/**
 * 📝 SUSCRIPCIÓN - Reclamar plan de prueba gratuito de 7 días
 * Activa el plan de prueba sin necesidad de pago
 * 
 * @param {number} idEmpresa - ID de la empresa
 * @returns {Promise<{message: string, plan: object}>} - Confirmación y datos del plan activado
 */
export const claimFreeTrial = createAsyncThunk(
  "suscripcion/claimFreeTrial",
  async ({ idEmpresa }, { rejectWithValue }) => {
    try {
      store.dispatch(setLoadingSuscripcion(true));
      
      // POST /suscripciones/claim-plan-free-test
      const response = await api.post(`/suscripciones/claim-plan-free-test`, {
        idEmpresa,
      });
      
      // La respuesta debe contener { message: "...", plan: {...} }
      if (!response.data?.plan) {
        throw new Error("No se recibió información del plan activado");
      }
      
      // Actualizar el plan actual en el estado
      store.dispatch(setPlanActual({
        planActivo: response.data.plan.activo,
        planTipo: response.data.plan.tipo,
        planVigencia: response.data.plan.vigencia,
      }));
      
      store.dispatch(suscripcionSuccess(response.data.message));
      store.dispatch(setLoadingSuscripcion(false));
      
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || 
        "Error al reclamar el plan de prueba gratuito";
      
      store.dispatch(suscripcionFailure(message));
      store.dispatch(setLoadingSuscripcion(false));
      
      return rejectWithValue({ message });
    }
  }
);

